import {Validators} from '@angular/forms';

import {WhitespaceValidator} from '@spa/validators';

import {ComponentTypesEnum} from '../../app/shared/enums';
import {BaseControl} from '../../app/shared/classes';
import {
    DialogButtonTypesEnum,
    DialogConfigInterface,
    ReportTableActionMenuTypesEnum,
    ReportTableActionsEnum,
    ReportTableButtonTypesEnum,
    ReportTableColumn,
    ReportTableConfigInterface
} from '../../app/shared/components';

// The class file rather than the barrel: the barrel reaches back into this descriptor collection
// through the panel base component and the import would close a cycle.
import {ConfirmationData} from 'appEntries/panel/classes/confirmation-data';

export const PanelCampaignManagementStorageId = 'PanelCampaignManagementComponent';

/**
 * Server-side limits of the campaign registry (Task 5), mirrored client-side so the operator is told
 * before the request leaves. The code is compared trimmed, so the trimmed value is what is bounded.
 */
export const CampaignCodeMaxLength = 200;
export const CampaignDescriptionMaxLength = 500;

/**
 * Column selectors - the snake_case field names of CampaignInterface.
 */
export const CampaignManagementColumnNames = {
    Id: 'id',
    Code: 'code',
    Description: 'description',
    IsActive: 'is_active'
};

/**
 * The registry list. Deactivated campaigns stay in it - they keep attributing their historical
 * clients - so the active flag is a column of its own and the row action is a toggle.
 *
 * The two directions of the toggle are declared once for the table; each row disables the one that
 * does not apply (`disabledActions`), so only "Reactivate" is live on a deactivated campaign and only
 * "Deactivate" on an active one. There is deliberately no delete action: the registry is
 * deactivate-never-delete and the backend exposes no DELETE endpoint.
 */
export const PanelCampaignManagementDataTableConfig = (): ReportTableConfigInterface => {
    return {
        rows: [],
        columns: [
            new ReportTableColumn({
                label: 'ID',
                selector: CampaignManagementColumnNames.Id
            }),
            new ReportTableColumn({
                label: 'Code',
                selector: CampaignManagementColumnNames.Code
            }),
            new ReportTableColumn({
                label: 'Description',
                selector: CampaignManagementColumnNames.Description
            }),
            new ReportTableColumn({
                label: 'Active',
                selector: CampaignManagementColumnNames.IsActive
            })
        ],
        hiddenColumnIds: [],
        disableColumnsOptions: true,
        actionMenu: true,
        actionMenuType: ReportTableActionMenuTypesEnum.Buttons,
        actionMenuItems: [
            {
                buttonType: ReportTableButtonTypesEnum.Approve,
                action: ReportTableActionsEnum.Approve
            },
            {
                buttonType: ReportTableButtonTypesEnum.Decline,
                action: ReportTableActionsEnum.Decline
            }
        ]
    };
};

export const CreateCampaignDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Create
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

export interface CreateCampaignFormControlsInterface {
    code: BaseControl;
    description: BaseControl;
}

/**
 * The code is immutable once the campaign exists and is what the funnel attributes against, so it is
 * the only mandatory field. `WhitespaceValidator` is the required check: a code of blanks trims to
 * nothing server-side and would be rejected there.
 */
export const CreateCampaignControls = (): CreateCampaignFormControlsInterface => ({
    code: new BaseControl({
        name: CampaignManagementColumnNames.Code,
        value: '',
        placeholder: 'Code',
        validators: [WhitespaceValidator(), Validators.maxLength(CampaignCodeMaxLength)],
        componentType: ComponentTypesEnum.Text
    }),
    description: new BaseControl({
        name: CampaignManagementColumnNames.Description,
        value: '',
        placeholder: 'Description',
        validators: [Validators.maxLength(CampaignDescriptionMaxLength)],
        componentType: ComponentTypesEnum.Textarea
    })
});

/**
 * Deactivation is not a removal and the wording says so: the campaign keeps every client already
 * attributed to it and only stops being offered when new clients are named.
 */
export const ConfirmDeactivateCampaignData = (code: string): ConfirmationData => {
    return new ConfirmationData(
        `Deactivate campaign ${code}`,
        'Deactivated campaigns keep attributing their historical clients; they stop appearing for new naming only.',
        [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Yes, Deactivate'
            }
        ],
        true
    );
};

export const ConfirmReactivateCampaignData = (code: string): ConfirmationData => {
    return new ConfirmationData(
        `Reactivate campaign ${code}`,
        'The campaign will be offered again when new clients are named.',
        [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Confirm,
                customText: 'Yes, Reactivate'
            }
        ],
        true
    );
};
